import React, { useState, useEffect } from "react";
import Modal from "../../common-components/Modal";
import TertiaryButton from "../../common-components/Buttons/TertiaryButton";
import { formatIST } from "./utils/dateUtils";
import { useTranslation } from 'react-i18next';
import { FaImage, FaFileVideo, FaFilePdf, FaLink, FaCalendarAlt } from "react-icons/fa";
import Skeleton from "react-loading-skeleton";

const CampaignPreviewModal = ({ isOpen = false, onClose = () => {}, campaign = null }) => {
  const { t } = useTranslation();
  const [mediaError, setMediaError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    if (campaign) {
      setMediaError(false);
      setImageLoading(true);
    }
  }, [campaign]);

  if (!isOpen || !campaign) return null;

  const getMediaUrl = () => {
    if (campaign?.presigned_url) return campaign.presigned_url;
    if (campaign?.file_url) return campaign.file_url;
    if (campaign?.media_url) return campaign.media_url;
    return null;
  };

  const getThumbnailUrl = () => {
    return campaign?.thumbnail_presigned_url || campaign?.thumbnail_url || null;
  };

  const mediaUrl = getMediaUrl();
  const thumbnailUrl = getThumbnailUrl();

  const fileType = campaign?.file_type || "";
  const isVideo =
    fileType.startsWith("video") ||
    (mediaUrl && /\.(mp4|avi|mov|wmv|flv|webm|mkv|ogg)$/i.test(mediaUrl));
  const isPdf =
    fileType === "application/pdf" ||
    (mediaUrl && mediaUrl.toLowerCase().endsWith(".pdf"));
  const isImage =
    fileType.startsWith("image") ||
    (mediaUrl && /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(mediaUrl));

  const getMediaIcon = () => {
    if (isVideo) return <FaFileVideo className="text-red-500 text-2xl" />;
    if (isPdf) return <FaFilePdf className="text-blue-500 text-2xl" />;
    return <FaImage className="text-green-500 text-2xl" />;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-700 border-green-200";
      case "inactive":
        return "bg-gray-100 text-gray-600 border-gray-200";
      case "draft":
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-600 border-gray-200";
    }
  };

  return (
    <Modal
      showModal={isOpen}
      onClose={onClose}
      modalHeight="h-[90%]"
      modalWidth="w-[80%]"
    >
      <div className="p-5 overflow-y-auto">
        <h2 className="text-xl font-bold text-[#4C6AF7] mb-4 flex items-center gap-2">
          {getMediaIcon()}
          <span>{campaign.title}</span>
        </h2>

        <div className="space-y-4">
          {/* Media Preview */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {t("Media Preview")}
            </h3>
            <div className="bg-gray-50 rounded-lg p-4">
              {!mediaUrl ? (
                <div className="text-center py-8">
                  <FaImage className="text-gray-400 text-4xl mx-auto mb-2" />
                  <p className="text-gray-500">{t("No media file available")}</p>
                </div>
              ) : isPdf ? (
                <div className="text-center">
                  {thumbnailUrl ? (
                    <>
                      {imageLoading && (
                        <div className="mb-2">
                          <Skeleton height={200} />
                        </div>
                      )}
                      <img
                        src={thumbnailUrl}
                        alt={campaign.title + " thumbnail"}
                        className={`w-full max-w-lg mx-auto rounded-lg shadow-sm object-contain max-h-96 ${
                          imageLoading ? "opacity-0" : "opacity-100"
                        }`}
                        onError={() => {
                          setMediaError(true);
                          setImageLoading(false);
                        }}
                        onLoad={() => setImageLoading(false)}
                      />
                      {mediaError && !imageLoading && (
                        <div className="mt-4">
                          <a
                            href={mediaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 bg-[#4C6AF7] hover:bg-[#3B5AE6] text-white rounded-lg font-medium transition"
                          >
                            <FaFilePdf className="mr-2" />
                            {t("Open PDF")}
                          </a>
                        </div>
                      )}
                    </>
                  ) : (
                    <a
                      href={mediaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-[#4C6AF7] hover:bg-[#3B5AE6] text-white rounded-lg font-medium transition"
                    >
                      <FaFilePdf className="mr-2" />
                      {t("Open PDF")}
                    </a>
                  )}
                </div>
              ) : isImage ? (
                <div>
                  {imageLoading && (
                    <div className="mb-2">
                      <Skeleton height={300} />
                    </div>
                  )}
                  <img
                    src={mediaUrl}
                    alt={campaign.title}
                    className={`w-full rounded-lg shadow-sm object-contain max-h-96 ${
                      imageLoading ? "opacity-0" : "opacity-100"
                    }`}
                    onError={() => {
                      setMediaError(true);
                      setImageLoading(false);
                    }}
                    onLoad={() => setImageLoading(false)}
                  />
                  {mediaError && !imageLoading && (
                    <div className="text-center py-4">
                      <FaImage className="text-gray-400 text-2xl mx-auto mb-2" />
                      <p className="text-gray-500">{t("Failed to load image")}</p>
                    </div>
                  )}
                </div>
              ) : isVideo ? (
                <div>
                  <video
                    controls
                    className="w-full rounded-lg shadow-sm max-h-96"
                    onError={() => setMediaError(true)}
                    preload="metadata"
                    poster={thumbnailUrl || undefined}
                  >
                    <source src={mediaUrl} type={fileType || "video/mp4"} />
                    {t("Your browser does not support the video tag.")}
                  </video>
                  {mediaError && (
                    <div className="text-center py-4">
                      <FaFileVideo className="text-gray-400 text-2xl mx-auto mb-2" />
                      <p className="text-gray-500">{t("Failed to load video")}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <a
                    href={mediaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-[#4C6AF7] hover:bg-[#3B5AE6] text-white rounded-lg font-medium transition"
                  >
                    {t("Open File")}
                  </a>
                  <p className="text-gray-500 text-sm mt-2">
                    {t("File preview is not supported. You can open or download the file.")}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Campaign Details */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {t("Campaign Details")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-gray-600">{t("Status")}</span>
                </div>
                <div
                  className={`text-sm font-semibold px-3 py-1 rounded-full inline-block ${getStatusColor(
                    campaign.status
                  )}`}
                >
                  {campaign.status?.toUpperCase() || "UNKNOWN"}
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-gray-600">{t("Price")}</span>
                </div>
                <div className="text-sm font-semibold text-gray-800">
                  {campaign.price ? `₹${campaign.price}` : t("Not set")}
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          {campaign.description && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {t("Description")}
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 leading-relaxed">{campaign.description}</p>
              </div>
            </div>
          )}

          {/* External Link */}
          {campaign.external_link && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {t("External Link")}
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 flex items-center">
                <FaLink className="text-gray-400 mr-3 flex-shrink-0" />
                <a
                  href={campaign.external_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#4C6AF7] hover:text-[#3B5AE6] hover:underline font-medium break-all"
                >
                  {campaign.external_link}
                </a>
              </div>
            </div>
          )}

          {/* Schedule */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">{t("Schedule")}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <FaCalendarAlt className="text-green-500" />
                  <span className="text-sm font-medium text-gray-600">{t("Start Date")}</span>
                </div>
                <div className="text-gray-800 font-medium">
                  {formatIST(campaign.start_date)}
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <FaCalendarAlt className="text-red-500" />
                  <span className="text-sm font-medium text-gray-600">{t("End Date")}</span>
                </div>
                <div className="text-gray-800 font-medium">
                  {formatIST(campaign.end_date)}
                </div>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {t("Additional Information")}
            </h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-600">{t("Campaign ID")}:</span>
                  <span className="ml-2 text-gray-800">{campaign.id}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">{t("Created")}:</span>
                  <span className="ml-2 text-gray-800">
                    {formatIST(campaign.created_at)}
                  </span>
                </div>
                {campaign.updated_at && (
                  <div>
                    <span className="font-medium text-gray-600">{t("Last Updated")}:</span>
                    <span className="ml-2 text-gray-800">
                      {formatIST(campaign.updated_at)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <TertiaryButton buttonName={t("Close")} onClick={onClose} />
        </div>
      </div>
    </Modal>
  );
};

export default CampaignPreviewModal;

